import PartiesIcon from "@mui/icons-material/People";
import WarningIcon from "@mui/icons-material/WarningAmber";
import {
  Badge,
  Box,
  Button,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { Big } from "big.js";
import { ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCase, useCaseSubmit } from "../../context/CaseContext";
import { useCurrency } from "../../context/CurrencyContext.tsx";
import { useTheme } from "../../context/ThemeContext";
import { CostType, Stage } from "../../shared/CaseConfiguration.ts";
import { PartyDescriptors } from "../../shared/CaseModel";
import { getCostsPerStage } from "../../shared/CaseModelUtility";
import { budgetHasCosts } from "../../shared/casePrerequisites";
import {
  lockedAt,
  NOTHING_LOCKED,
  stageShareOf,
} from "../../shared/costChanges";
import { Budget } from "../../shared/CostModel";
import { percentage } from "../../shared/percentage.ts";
import { getCostSummary } from "../CostSummaryValues.ts";
import { byName, clientsFirst, sort } from "../analytics/AnalyticsUtility";
import { getColorForCosts } from "../analytics/CostColors.ts";
import CenteredMessageBox from "../general/CenteredMessageBox.tsx";
import { Breakdown, Metric } from "../general/DataAtoms.tsx";
import { TABULAR_NUMERALS } from "../general/dataAtoms/shared.ts";
import { SectionLabel } from "../general/SectionLabel.tsx";
import { GuideHeader } from "../guide/GuideHeader.tsx";
import { GuideMarker, Marked } from "../guide/GuideMarker.tsx";
import { Tabs } from "../general/Tabs";
import { TonalBadge } from "../general/TonalBadge.tsx";
import { getDisbursementsTotal } from "./BudgetUtils.ts";
import { CostsBarChart } from "./CostsBarChart.tsx";
import { CostsEditorBudgetAdapter } from "./CostsEditorBudgetAdapter.tsx";
import CostsPerStageSlider from "./CostsPerStageSlider.tsx";
import { PartyVat } from "./PartyVat.tsx";
import { useStageClosing } from "../timeline/useStageClosing.tsx";

export interface BudgetCallbacks {
  update(newValue: Budget): Promise<void>;
  /** sets the rate of VAT the party pays, none when it reclaims its VAT */
  updateVatRate(partyId: string, vatRate: number | undefined): Promise<void>;
}

export type CostCriteria = "byCostType" | "bySpending";

export const Budgeting = () => {
  const caseContext = useCase();
  const { budgets, parties: caseParties } = caseContext.getCase();
  const parties: PartyDescriptors = caseContext.getPartyDescriptors();

  const submit = useCaseSubmit();
  const budgetCallbacks: BudgetCallbacks = {
    update: function (budget: Budget): Promise<void> {
      return submit({ budget: JSON.stringify(budget) }, { method: "put" });
    },
    updateVatRate: function (partyId, vatRate): Promise<void> {
      const party = caseParties?.find(({ id }) => id === partyId);
      if (party === undefined) return Promise.resolve();
      // an undefined rate leaves the field out: the party reclaims its VAT
      return submit(
        { party: JSON.stringify({ ...party, vatRate }) },
        { method: "put" },
      );
    },
  };

  return (
    <BudgetingComponent
      budgets={budgets || []}
      budgetCallbacks={budgetCallbacks}
      parties={parties}
    />
  );
};

/**
 * An amount of the costs spent or of those still to come, named beside the
 * colour it has in the charts of the page.
 */
const SpendingFigure = ({
  color,
  label,
  value,
  dashed = false,
}: {
  color: string;
  label: string;
  value: string;
  // for an amount drawn as a dashed outline
  dashed?: boolean;
}) => (
  <Box>
    <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
      <Box
        sx={{
          width: 11,
          height: 11,
          borderRadius: 0.75,
          boxSizing: "border-box",
          ...(dashed
            ? { border: `1.5px dashed ${color}` }
            : { bgcolor: color }),
          flex: "none",
        }}
      />
      <Typography
        variant="caption"
        sx={{ color: "text.secondary", fontWeight: 500 }}
      >
        {label}
      </Typography>
    </Stack>
    <Typography variant="h6" sx={{ fontWeight: 700, ...TABULAR_NUMERALS }}>
      {value}
    </Typography>
  </Box>
);

export const BudgetingComponent = ({
  budgets,
  budgetCallbacks,
  parties,
}: {
  budgets: Budget[];
  budgetCallbacks: BudgetCallbacks;
  parties: PartyDescriptors;
}) => {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const [sliderPaddingLeft, setSliderPaddingLeft] = useState(theme.spacing(7));
  const { currency } = useCurrency();
  const sortedParties: string[] = useMemo(
    () => sort(parties, clientsFirst, byName),
    [parties],
  );

  const isSmallDisplay = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const caseContext = useCase();
  // where the case stands: the timeline's open stage, which the slider
  // moves within
  const stageClosing = useStageClosing();
  const caseId = caseContext.getCase().id;

  const header = (
    <GuideHeader>
      <Box>
        <Typography
          variant="h5"
          component="h1"
          sx={{ fontWeight: 700, letterSpacing: "-.01em" }}
        >
          {t("case.budget.title")}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", mt: 0.5, maxWidth: "72ch" }}
        >
          {t("case.budget.description")}
        </Typography>
      </Box>
    </GuideHeader>
  );

  if (sortedParties.length === 0) {
    return (
      <Stack spacing={2.5}>
        {header}
        <CenteredMessageBox
          message={t("case.budget.noParties")}
          action={{
            label: t("case.budget.goToParties"),
            icon: <PartiesIcon />,
            onClick: () => navigate(`/cases/${caseId}/configuration/parties`),
          }}
        />
      </Stack>
    );
  }

  const card = { borderRadius: 3, overflow: "hidden" } as const;
  const incurredColor = getColorForCosts("incurred", theme).backgroundColor;
  const estimatedColor = getColorForCosts("estimated", theme).backgroundColor;
  const notToBeSpentColor = getColorForCosts("notToBeSpent", theme)
    .borderColor;
  const stagesOnSlider = caseContext
    .getStages()
    .filter((stage) => stage !== Stage.Appeals);

  const tabs: { label: ReactNode; tabComponent: ReactNode }[] =
    sortedParties.map((partyId, index) => {
      const party = parties[partyId];
      const name = party?.name || partyId;
      const budget = budgets.find((b) => b.partyId === partyId);
      // the drawer's costs dot reads the same check, so they cannot disagree
      const isMissingCosts = !budget || !budgetHasCosts(budget);

      const label = (
        <Badge
          color="warning"
          variant="standard"
          badgeContent={isMissingCosts ? "?" : undefined}
          sx={(theme) => ({
            "& .MuiBadge-badge": { right: theme.spacing(-1) },
          })}
        >
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <span>{name}</span>
            {party?.isClient && (
              <TonalBadge tone={theme.palette.primary}>
                {t("case.parties.clientBadge")}
              </TonalBadge>
            )}
          </Stack>
        </Badge>
      );

      if (!budget) {
        return {
          label,
          tabComponent: (
            <Typography>{t("case.budget.noBudget", { name })}</Typography>
          ),
        };
      }
      const vatRate = caseContext
        .getCase()
        .parties?.find(({ id }) => id === partyId)?.vatRate;
      const costSummary = getCostSummary(budget, vatRate);
      // what the party's settlements locked: what of it was incurred stays
      // spent, the rest is not to be spent (R4 and R12 of
      // SettlementFreezeOnSave.test.ts)
      const locked =
        caseContext.getCase().results?.lockedCosts?.[partyId] ??
        NOTHING_LOCKED;
      // the open stage, and what of its open costs is incurred
      const stage = stageClosing.open;
      const stageShare =
        (stage !== undefined && stageShareOf(budget, locked, stage)) ||
        new Big(0);
      const notToBeSpent = lockedAt(locked).estimated.toNumber();
      const spent = {
        incurred: costSummary.incurredCosts,
        estimated: costSummary.estimatedCosts - notToBeSpent,
        notToBeSpent,
      };
      // what of a cost type is spent, and what is not to be spent
      const ofType = (type: CostType) => ({
        part:
          type === "lawFirmFee"
            ? costSummary.incurredByType.lawFirmFees
            : costSummary.incurredByType.disbursements[type],
        endPart: lockedAt(locked, undefined, type).estimated.toNumber(),
      });
      // the Cost progress: what of the budget as entered it comes to
      const shareOfBudget = (amount: number) =>
        costSummary.totalCosts > 0 ? amount / costSummary.totalCosts : 0;

      const incurred = (
        <SpendingFigure
          color={incurredColor}
          label={t("case.costs.incurred")}
          value={currency.format(spent.incurred)}
        />
      );
      const estimated = (
        <SpendingFigure
          color={estimatedColor}
          label={t("case.costs.budgeted")}
          value={currency.format(spent.estimated)}
        />
      );
      const notToBeSpentFigure = spent.notToBeSpent > 0 && (
        <SpendingFigure
          color={notToBeSpentColor}
          dashed
          label={t("case.costs.notToBeSpent")}
          value={currency.format(spent.notToBeSpent)}
        />
      );

      return {
        label,
        tabComponent: (
          <Stack spacing={2.25}>
            {/* Cost editor */}
            <Paper variant="outlined" sx={card}>
              <Stack
                direction="row"
                spacing={2}
                useFlexGap
                sx={{
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  px: 2.5,
                  pt: 2,
                  pb: 2.25,
                }}
              >
                <Box>
                  <Marked entry="editor">
                    <SectionLabel component="h2">
                      {t("case.budget.section.editor")}
                    </SectionLabel>
                  </Marked>
                  <Stack
                    direction="row"
                    spacing={4.25}
                    useFlexGap
                    sx={{ flexWrap: "wrap", mt: 1.5 }}
                  >
                    <Metric
                      label={t(
                        vatRate === undefined
                          ? "CostSummary.total"
                          : "CostSummary.totalWithVat",
                      )}
                      value={currency.format(
                        costSummary.totalCosts + costSummary.vat,
                      )}
                    />
                    <Metric
                      label={t("CostSummary.lawFirmFees")}
                      value={currency.format(costSummary.lawFirmFees)}
                    />
                    <Metric
                      label={t("CostSummary.disbursements")}
                      value={currency.format(
                        getDisbursementsTotal(
                          costSummary.disbursements,
                        ).toNumber(),
                      )}
                    />
                    {vatRate !== undefined && (
                      <Metric
                        label={t("CostSummary.vat", {
                          rate: percentage.format(vatRate),
                        })}
                        value={currency.format(costSummary.vat)}
                      />
                    )}
                  </Stack>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: "center", mt: 2 }}
                  >
                    <PartyVat
                      name={name}
                      vatRate={vatRate}
                      onChange={(newRate) =>
                        budgetCallbacks.updateVatRate(partyId, newRate)
                      }
                    />
                    <GuideMarker entry="vat" />
                  </Stack>
                </Box>
                {isMissingCosts && (
                  <TonalBadge tone={theme.palette.warning} sx={{ py: 0.5 }}>
                    <WarningIcon fontSize="inherit" />
                    {t("case.budget.warning.noCostsDefined")}
                  </TonalBadge>
                )}
              </Stack>
              <CostsEditorBudgetAdapter
                budget={budget}
                otherBudgets={budgets.filter(
                  (b) => b.partyId !== budget.partyId,
                )}
                updateBudget={budgetCallbacks.update}
                key={"budget_" + index}
              />
            </Paper>

            {/* Spending progress */}
            <Paper variant="outlined" sx={{ ...card, p: 2.5 }}>
              <Stack
                direction="row"
                spacing={2}
                useFlexGap
                sx={{
                  alignItems: "flex-end",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  mb: 2,
                }}
              >
                <Box>
                  <Marked entry="progress">
                    <SectionLabel component="h2">
                      {t("case.budget.section.progress")}
                    </SectionLabel>
                  </Marked>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, letterSpacing: "-.01em", mt: 1 }}
                  >
                    {stage === undefined
                      ? t("case.budget.progress.allClosed")
                      : t("case.budget.progress.summary", {
                          incurred: percentage.format(stageShare.toNumber()),
                          stage: t(`case.stages.${Stage[stage]}`),
                        })}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {t("case.budget.progress.ofBudget", {
                      share: percentage.format(shareOfBudget(spent.incurred)),
                    })}
                    {spent.notToBeSpent > 0 &&
                      " " +
                        t("case.budget.progress.notToBeSpent", {
                          share: percentage.format(
                            shareOfBudget(spent.notToBeSpent),
                          ),
                        })}
                  </Typography>
                </Box>
                <Stack
                  direction="row"
                  spacing={3.5}
                  useFlexGap
                  sx={{ flexWrap: "wrap" }}
                >
                  {incurred}
                  {estimated}
                  {notToBeSpentFigure}
                </Stack>
              </Stack>
              <CostsBarChart
                data={getCostsPerStage(budget).filter(
                  ({ stage }) => stage !== Stage.Appeals,
                )}
                criteria={"bySpending"}
                locked={locked}
                showYLabels={!isSmallDisplay}
                onFit={(xAxisStart) => setSliderPaddingLeft(`${xAxisStart}px`)}
              />
              {/* the slider starts where the chart's stages do */}
              <Box
                sx={{
                  pl: sliderPaddingLeft,
                }}
              >
                <CostsPerStageSlider
                  // anew where the open stage changes: it starts where it is
                  key={stage ?? "closed"}
                  budget={budget}
                  locked={locked}
                  openStage={stage}
                  updateBudget={budgetCallbacks.update}
                  onCloseRequested={stageClosing.requestClose}
                  onReopenRequested={stageClosing.requestReopen}
                  stepsPerStage={isSmallDisplay ? 2 : 4}
                />
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {t("case.budget.progress.tooltip")}
                </Typography>
                {!caseContext.readOnly && (
                  <Stack
                    direction="row"
                    spacing={1}
                    useFlexGap
                    sx={{ flexWrap: "wrap", mt: 1.5 }}
                  >
                    {stage !== undefined && (
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{ textTransform: "none", fontWeight: 600 }}
                        onClick={stageClosing.requestClose}
                      >
                        {t("case.timeline.close", {
                          stage: t(`case.stages.${Stage[stage]}`),
                        })}
                      </Button>
                    )}
                    {stageClosing.lastClosed !== undefined && (
                      <Button
                        size="small"
                        sx={{ textTransform: "none", fontWeight: 600 }}
                        onClick={stageClosing.requestReopen}
                      >
                        {t("case.timeline.reopen", {
                          stage: t(
                            `case.stages.${Stage[stageClosing.lastClosed]}`,
                          ),
                        })}
                      </Button>
                    )}
                  </Stack>
                )}
                {stageClosing.dialogs}
              </Box>
            </Paper>

            {/* Cost structure */}
            <Paper variant="outlined" sx={{ ...card, p: 2.5 }}>
              <Marked entry="breakdown">
                <SectionLabel component="h2">
                  {t("case.budget.section.structure")}
                </SectionLabel>
              </Marked>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: { xs: 3, md: 5 },
                  mt: 1.75,
                }}
              >
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, mb: 1.25 }}
                  >
                    {t("case.budget.criteria.bySpending")}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={3.5}
                    useFlexGap
                    sx={{ flexWrap: "wrap" }}
                  >
                    {incurred}
                    {estimated}
                    {notToBeSpentFigure}
                  </Stack>
                  {/* the three as shares of all the party's costs */}
                  <Box
                    sx={{
                      display: "flex",
                      height: theme.spacing(1.25),
                      borderRadius: 3,
                      overflow: "hidden",
                      bgcolor: theme.palette.action.hover,
                      mt: 1.75,
                    }}
                  >
                    {[
                      {
                        costs: spent.incurred,
                        color: incurredColor,
                        dashed: false,
                      },
                      {
                        costs: spent.estimated,
                        color: estimatedColor,
                        dashed: false,
                      },
                      {
                        costs: spent.notToBeSpent,
                        color: notToBeSpentColor,
                        dashed: true,
                      },
                    ].map(({ costs, color, dashed }) => (
                      <Box
                        key={color}
                        sx={{
                          width:
                            costSummary.totalCosts > 0
                              ? percentage.format(
                                  costs / costSummary.totalCosts,
                                )
                              : 0,
                          boxSizing: "border-box",
                          ...(dashed
                            ? {
                                border: costs > 0 ? `1px dashed ${color}` : 0,
                                borderRadius: 3,
                              }
                            : { bgcolor: color }),
                        }}
                      />
                    ))}
                  </Box>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {t("case.budget.criteria.byCostType")}
                  </Typography>
                  <Breakdown
                    // each bar says what of its costs is spent, in the
                    // colours the two amounts beside it have
                    split={{
                      label: t("case.costs.incurred"),
                      color: incurredColor,
                      restColor: estimatedColor,
                      end: {
                        label: t("case.costs.notToBeSpent"),
                        color: notToBeSpentColor,
                      },
                    }}
                    items={[
                      {
                        label: t("case.costs.costTypes.lawFirmFee"),
                        value: costSummary.lawFirmFees,
                        ...ofType("lawFirmFee"),
                      },
                      ...(
                        [
                          "courtFee",
                          "barristerFee",
                          "expertFee",
                          "otherDisbursement",
                        ] as const
                      ).map((type) => ({
                        label: t(`case.costs.costTypes.${type}`),
                        value: costSummary.disbursements[type],
                        ...ofType(type),
                      })),
                    ]}
                  />
                </Box>
              </Box>
            </Paper>
          </Stack>
        ),
      };
    });

  return (
    <Stack spacing={1.5}>
      {header}
      <Box>
        <Tabs tabs={tabs} />
      </Box>
    </Stack>
  );
};
